import React, { useEffect, useRef, useState } from "react";
import { Field, Form, Formik } from "formik";
import moment from "moment";
import { Col, Row } from "react-bootstrap";
import MainContainer from "pages/_components/MainContainer";
import Selector from "pages/_components/fields/formik/Selector";
import AmountField from "pages/_components/fields/formik/AmountField";
import TextField from "pages/_components/fields/TextField";
import Date from "pages/_components/fields/DateField";
import Button from "pages/_components/Button";
import { actions as accountsActions } from "reducers/accounts";
import { actions as notificationActions } from "reducers/notification";
import * as i18n from "util/i18n";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import CustomText from "pages/_components/CustomText/CustomText";
import Notification from "pages/_components/Notification";
import { ResponsiveHeader } from "pages/_components/header";
import GridTable from "pages/_components/GridTable/GridTable";
import { useAgreementForm } from "./hooks/useAgreementForm";
import * as config from "util/config";

const FORM_ID = "account.agreement";

const AgreementRequestPage = () => {
    const formRef = useRef(null);
    const { accounts, agreementAvailableAmount, agreementTax } = useSelector((state) => state.accounts);
    const { id } = useParams();
    const accountSelected = accounts.find((selected) => selected.productId === id);

    const { fetching, options, initialValues, agreementValidation, owner, dispatch, history } = useAgreementForm(
        accountSelected?.currency,
    );
    const { accountOptions, expirationDayOptions, purposeOptions } = options;

    const [maxDays, setMaxDays] = useState(0);
    const defaultTZ = config.get("administration.timezone.defaultValue", "America/Argentina/Buenos_Aires");

    const handleSubmit = (values) => {
        if (agreementAvailableAmount < values.agreementToRequest.amount) {
            dispatch(
                notificationActions.showNotification(i18n.get("accounts.agreements.notification.limit"), "warning", [
                    "agreementRequestPage",
                ]),
            );
            return;
        }
        const selectedAccount = accounts.find((selected) => selected.productId === values.account);

        // Le configuro la hora final del dia 23:59:59 en TZ de Argentina
        const fechaFin = moment(values.expirationDate)
            .tz(defaultTZ)
            .endOf("day");

        const newAgreement = {
            account: {
                idProduct: selectedAccount.productId,
                productType: selectedAccount.accountType,
                number: selectedAccount.accountNumber,
            },
            amount: values.agreementToRequest.amount,
            purpose: values.purpose,
            expirationDate: fechaFin.toISOString(),
            tax: Number(values.rate),
            commission: parseFloat(values.commission.replace("%", "")),
            owner,
            currency: selectedAccount.currency,
        };

        dispatch(accountsActions.setNewAgreement(newAgreement));
        history.push(`/accounts/agreements/summaryForm`);
    };

    useEffect(() => {
        dispatch(accountsActions.fetchAgreementAvailableAmount());

        const expirationDay = config.getArray("account.agreement.expirationDays", ["7", "14", "21", "31"]);
        const numArr = expirationDay.map(Number); // Convert to number array
        const max = Math.max(...numArr);
        setMaxDays(max);
    }, [dispatch]);

    useEffect(() => {
        if (agreementAvailableAmount === 0) {
            dispatch(
                notificationActions.showNotification(i18n.get("accounts.agreements.notification.noLine"), "warning", [
                    "agreementRequestPage",
                ]),
            );
        }
    }, [agreementAvailableAmount]);

    useEffect(() => {
        if (!formRef.current) {
            return;
        }
        formRef.current.setFieldValue("rate", agreementTax);
    }, [agreementTax]);

    return (
        <>
            <Notification scopeToShow="agreementRequestPage" />
            <ResponsiveHeader id="account.agreement.request" />
            <MainContainer showLoader={fetching}>
                <GridTable className="box box-primary ">
                    <GridTable.Container columns={2} rows={1}>
                        <GridTable.Data className="balance-label" columnStart={1}>
                            <CustomText text="accounts.agreements.totalAvailableBalance" textType="I18N" />
                        </GridTable.Data>
                        <GridTable.Data columnStart={2} alignX="right">
                            <CustomText
                                text={agreementAvailableAmount}
                                textType={accountSelected?.currency}
                                className="content-data-strong"
                            />
                        </GridTable.Data>
                    </GridTable.Container>
                </GridTable>
                <Formik
                    ref={formRef}
                    enableReinitialize
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    validationSchema={agreementValidation}>
                    {({ setFieldValue, values }) => (
                        <Form>
                            <Row className="form-background agreement-request-table">
                                <Col xs={12} md={5} mdOffset={1} className="required">
                                    <Field
                                        idForm={FORM_ID}
                                        name="agreementToRequest"
                                        component={AmountField}
                                        data={{
                                            options: [
                                                {
                                                    id: 1,
                                                    label: i18n.get(`currency.label.${accountSelected?.currency}`),
                                                },
                                            ],
                                        }}
                                        clearable={false}
                                        labelText={i18n.get("account.agreement.toRequest")}
                                        onBlurChange={() => {
                                            if (values.expirationDate) {
                                                const dateTo = moment(values.expirationDate)
                                                    .tz(defaultTZ)
                                                    .endOf("day");

                                                return dispatch(
                                                    accountsActions.fetchAgreementTax(
                                                        accountSelected?.currency,
                                                        dateTo.toISOString(),
                                                        values.agreementToRequest.amount || 0,
                                                    ),
                                                );
                                            }
                                        }}
                                    />
                                </Col>
                                <Col xs={12} md={5} mdOffset={6}>
                                    <Field
                                        idForm={FORM_ID}
                                        name="account"
                                        label={i18n.get("account.agreement.account")}
                                        placeholder={i18n.get("global.select.account.placeholder")}
                                        component={Selector}
                                        options={accountOptions}
                                        disabled
                                        onChange={(value) => {
                                            setFieldValue("currency", value.currency);
                                        }}
                                    />
                                </Col>
                                <Col xs={12} md={5} mdOffset={1}>
                                    <Field
                                        idForm={FORM_ID}
                                        name="currency"
                                        component={TextField}
                                        hidePlaceholder
                                        type="text"
                                        labelText="account.agreement.currency"
                                        disabled
                                    />
                                </Col>
                                <Col xs={12} md={5} mdOffset={6}>
                                    <Field
                                        component={Date}
                                        idForm={FORM_ID}
                                        name="expirationDate"
                                        labelKey={i18n.get("account.agreement.expirationDay")}
                                        labelText={i18n.get("account.agreement.expirationDay")}
                                        hidePlaceholder
                                        startDate={new Date()}
                                        minDate={new Date()}
                                        maxDate={moment().add(maxDays, "days")}
                                        noEdit
                                        handleChange={(date) => {
                                            const dateTo = moment(date || moment().toDate())
                                                .tz(defaultTZ)
                                                .endOf("day");

                                            return dispatch(
                                                accountsActions.fetchAgreementTax(
                                                    accountSelected?.currency,
                                                    dateTo.toISOString(),
                                                    values.agreementToRequest.amount || 0,
                                                ),
                                            );
                                        }}
                                    />
                                </Col>
                                <Col xs={12} md={5} mdOffset={1}>
                                    <Field
                                        idForm={FORM_ID}
                                        name="purpose"
                                        label={i18n.get("account.agreement.purposes")}
                                        placeholder={i18n.get("global.select.option.placeholder")}
                                        component={Selector}
                                        options={purposeOptions}
                                    />
                                </Col>
                                <Col xs={12} md={5} mdOffset={6}>
                                    <Field
                                        idForm={FORM_ID}
                                        name="rate"
                                        component={TextField}
                                        hidePlaceholder
                                        type="text"
                                        labelText="account.agreement.ratePercentage"
                                        disabled
                                    />
                                </Col>
                                <Col xs={12} md={5} mdOffset={1}>
                                    <Field
                                        idForm={FORM_ID}
                                        name="commission"
                                        component={TextField}
                                        hidePlaceholder
                                        type="text"
                                        labelText="account.agreement.originationFee"
                                        disabled
                                    />
                                </Col>
                            </Row>
                            <Row className="form-actions">
                                <Col xs={12} md={4} mdOffset={2}>
                                    <Button
                                        className="btn-outline"
                                        label="global.cancel"
                                        onClick={() => history.push(`/accounts/${id}/agreements`)}
                                    />
                                </Col>
                                <Col xs={12} md={4} mdOffset={6}>
                                    <Button type="submit" className="btn-primary" label="global.continue" />
                                </Col>
                            </Row>
                        </Form>
                    )}
                </Formik>
            </MainContainer>
        </>
    );
};

export default AgreementRequestPage;
